<template>
  <main class="signup-page">
    <header class="signup-topbar">
      <router-link class="brand" to="/">
        <img class="brand-mark" src="/img/boarua-icon-concept-v1.png" alt="" />
        <span class="brand-text">BOARUA</span>
      </router-link>

      <router-link class="signup-back" to="/">
        <v-icon size="18">mdi-arrow-left</v-icon>
        Voltar
      </router-link>
    </header>

    <section class="signup-shell">
      <div class="signup-copy">
        <span class="eyebrow">Comece em poucos minutos</span>
        <h1>Crie a conta do seu local</h1>
        <p>
          Cadastre o condomínio, rua, loteamento ou bloco, defina o administrador principal e acesse o
          painel para configurar convites, demandas e mensalidades.
        </p>
        <div class="signup-highlights">
          <span><v-icon size="18">mdi-shield-home-outline</v-icon> Segurança</span>
          <span><v-icon size="18">mdi-barcode-scan</v-icon> Cobranças</span>
          <span><v-icon size="18">mdi-map-marker-radius-outline</v-icon> Operação</span>
        </div>
      </div>

      <v-form ref="form" v-model="valid" class="signup-form" lazy-validation @submit.prevent="submit">
        <div class="form-header">
          <span class="form-icon"><v-icon color="primary">mdi-home-city-outline</v-icon></span>
          <div>
            <h2>Dados iniciais</h2>
            <p>Configure o local e o responsável de acesso.</p>
          </div>
        </div>

        <v-text-field
          v-model="form.association_name"
          :rules="[$rules.required]"
          class="signup-field"
          label="Nome público do local"
          hide-details="auto"
          outlined
        />

        <v-row dense>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="form.legal_name"
              :rules="[$rules.required]"
              class="signup-field"
              label="Razão social"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="form.document"
              v-mask="$masks.cnpj"
              :rules="[$rules.required, $rules.cnpj]"
              class="signup-field"
              label="CNPJ"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-file-input
              v-model="form.company_document_file"
              :rules="[$rules.required, pdfFileRule, $rules.fileSize(5)]"
              accept="application/pdf,.pdf"
              class="signup-field"
              label="Doc. da Empresa"
              hide-details="auto"
              outlined
              prepend-icon=""
              prepend-inner-icon="mdi-file-document-outline"
              show-size
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.postal_code"
              v-mask="$masks.zipCode"
              :loading="cepLoading"
              class="signup-field"
              label="CEP"
              hide-details="auto"
              outlined
              @blur="loadAddressByCep"
            />
          </v-col>
          <v-col cols="12" md="7">
            <v-text-field v-model="form.address" class="signup-field" label="Endereço fiscal" hide-details="auto" outlined />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="form.address_number" class="signup-field" label="Nº" hide-details="auto" outlined />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="form.neighborhood"
              class="signup-field"
              label="Bairro ou loteamento"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.city" class="signup-field" label="Cidade" hide-details="auto" outlined />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="form.state" class="signup-field" label="UF" maxlength="2" hide-details="auto" outlined />
          </v-col>
        </v-row>

        <v-divider class="my-2 mb-5" />

        <div class="section-title">
          <v-icon size="19" color="primary">mdi-account-key-outline</v-icon>
          Responsável
        </div>

        <v-text-field
          v-model="form.admin_name"
          :rules="[$rules.required]"
          class="signup-field"
          label="Nome completo"
          hide-details="auto"
          outlined
        />
        <v-text-field
          v-model="form.admin_document"
          v-mask="$masks.cpf"
          :rules="[$rules.required, $rules.cpf]"
          class="signup-field"
          label="CPF"
          hide-details="auto"
          outlined
        />
        <v-file-input
          v-model="form.responsible_document_file"
          :rules="[$rules.required, pdfFileRule, $rules.fileSize(5)]"
          accept="application/pdf,.pdf"
          class="signup-field"
          label="Doc. do Responsável"
          hide-details="auto"
          outlined
          prepend-icon=""
          prepend-inner-icon="mdi-file-document-outline"
          show-size
        />
        <v-text-field
          v-model="form.admin_email"
          :rules="[$rules.required]"
          class="signup-field"
          label="E-mail de acesso"
          hide-details="auto"
          outlined
          type="email"
        />
        <v-text-field
          v-model="form.phone"
          v-mask="$masks.phone"
          class="signup-field"
          label="Celular"
          hide-details="auto"
          outlined
        />
        <v-text-field
          v-model="form.admin_password"
          :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :rules="[$rules.required, signupPasswordRule]"
          :type="showPassword ? 'text' : 'password'"
          class="signup-field"
          label="Senha"
          hide-details="auto"
          outlined
          @click:append="showPassword = !showPassword"
        />
        <v-text-field
          v-model="form.admin_password_confirmation"
          :append-icon="showPasswordConfirmation ? 'mdi-eye-off' : 'mdi-eye'"
          :rules="[$rules.required, passwordConfirmationRule]"
          :type="showPasswordConfirmation ? 'text' : 'password'"
          class="signup-field"
          label="Confirmar senha"
          hide-details="auto"
          outlined
          @click:append="showPasswordConfirmation = !showPasswordConfirmation"
        />

        <v-btn
          block
          class="signup-submit"
          color="primary"
          depressed
          height="56"
          :loading="loading"
          type="submit"
        >
          Criar conta e verificar e-mail
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <p class="login-note">
          Ja possui conta?
          <router-link to="/login">Entrar no painel</router-link>
        </p>
      </v-form>
    </section>
  </main>
</template>

<script>
import axios from "axios";

export default {
  name: "Signup",
  data: () => ({
    valid: true,
    loading: false,
    cepLoading: false,
    showPassword: false,
    showPasswordConfirmation: false,
    form: {
      association_name: "",
      legal_name: "",
      document: "",
      company_document_file: null,
      responsible_document_file: null,
      neighborhood: "",
      city: "",
      state: "",
      address: "",
      address_number: "",
      postal_code: "",
      admin_name: "",
      admin_document: "",
      admin_email: "",
      phone: "",
      admin_password: "",
      admin_password_confirmation: "",
    },
  }),
  computed: {
    signupPasswordRule() {
      return (value) => {
        if (!value) return true;
        if (value.length < 8) return "Senha deve possuir pelo menos 8 caracteres";
        if (!/[0-9]/.test(value)) return "Senha precisa conter pelo menos um número";
        if (!/[^A-Za-z0-9]/.test(value)) return "Senha precisa conter pelo menos um caractere especial";
        return true;
      };
    },
    passwordConfirmationRule() {
      return (value) => value === this.form.admin_password || "As senhas não conferem";
    },
    pdfFileRule() {
      return (file) => {
        if (!file) return true;
        const name = String(file.name || "").toLowerCase();
        return file.type === "application/pdf" || name.endsWith(".pdf") || "Envie um arquivo PDF";
      };
    },
  },
  methods: {
    async loadAddressByCep() {
      const cep = String(this.form.postal_code || "").replace(/\D/g, "");

      if (cep.length !== 8) return;

      this.cepLoading = true;

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (!response.ok || data.erro) {
          this.$dialog.notify.warning("CEP não encontrado.", {
            position: "top-right",
            timeout: 3000,
          });
          return;
        }

        this.form.address = data.logradouro || this.form.address;
        this.form.neighborhood = data.bairro || this.form.neighborhood;
        this.form.city = data.localidade || this.form.city;
        this.form.state = data.uf || this.form.state;
      } catch (error) {
        this.$dialog.notify.error("Não foi possível consultar o CEP.", {
          position: "top-right",
          timeout: 3000,
        });
      } finally {
        this.cepLoading = false;
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const payload = this.buildPayload();

        const result = await axios.post("/onboarding/association-account", payload);
        const content = result.data.content || {};

        this.$dialog.notify.success(
          content.email_sent
            ? "Conta criada. Enviamos o codigo de verificacao por e-mail."
            : "Conta criada. Configure o envio de e-mail para receber o codigo.",
          { position: "top-right", timeout: 5000 }
        );
        this.$router.push({ path: "/verificar-conta", query: { email: this.form.admin_email } });
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: "top-right",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    buildPayload() {
      const payload = Object.assign({}, this.form, {
        document: this.onlyDigits(this.form.document),
        admin_document: this.onlyDigits(this.form.admin_document),
        phone: this.onlyDigits(this.form.phone),
        postal_code: this.onlyDigits(this.form.postal_code),
      });
      delete payload.admin_password_confirmation;

      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      });

      return formData;
    },
  },
};
</script>

<style scoped lang="scss">
.signup-page {
  background:
    radial-gradient(circle at 72% 18%, rgba(14, 163, 91, 0.08), transparent 30%),
    linear-gradient(135deg, #f7fbf8 0%, #eef6f1 100%);
  color: #111827;
  min-height: 100vh;
  overflow-x: hidden;
  padding: clamp(18px, 3vw, 34px) clamp(18px, 5vw, 80px) 48px;
}

.brand {
  align-items: center;
  color: #111827;
  display: inline-flex;
  gap: 14px;
  text-decoration: none;
}

.signup-topbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 18px;
}

.signup-back {
  align-items: center;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(5, 122, 61, 0.14);
  border-radius: 999px;
  color: #123024;
  display: inline-flex;
  font-size: 13px;
  font-weight: 900;
  gap: 7px;
  min-height: 38px;
  padding: 0 15px 0 13px;
  text-decoration: none;

  .v-icon {
    color: #057a3d;
  }
}

.brand-mark {
  display: block;
  height: 54px;
  object-fit: contain;
  width: 54px;
}

.brand-text {
  font-size: 24px;
  font-weight: 900;
  line-height: 1;
}

.signup-shell {
  align-items: start;
  display: grid;
  gap: clamp(30px, 5.5vw, 86px);
  grid-template-columns: minmax(300px, 430px) minmax(0, 640px);
  margin: clamp(34px, 7vh, 72px) auto 0;
  max-width: 1180px;
  width: 100%;
}

.signup-copy {
  padding-top: clamp(18px, 5vh, 48px);
}

.eyebrow {
  color: #057a3d;
  display: inline-block;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  margin-bottom: 16px;
  text-transform: uppercase;
}

h1 {
  color: #08111f;
  font-size: clamp(46px, 5.2vw, 78px);
  font-weight: 900;
  line-height: 1;
  margin: 0 0 24px;
  max-width: 520px;
}

.signup-copy p {
  color: #5f6a64;
  font-size: 18px;
  line-height: 1.5;
  margin: 0;
  max-width: 470px;
}

.signup-highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;

  span {
    align-items: center;
    background: rgba(255, 255, 255, 0.78);
    border: 1px solid rgba(5, 122, 61, 0.12);
    border-radius: 999px;
    color: #1f3529;
    display: inline-flex;
    font-size: 13px;
    font-weight: 800;
    gap: 7px;
    min-height: 36px;
    padding: 0 14px;
  }

  .v-icon {
    color: #057a3d;
  }
}

.signup-form {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(5, 122, 61, 0.16);
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(8, 17, 31, 0.08);
  padding: clamp(22px, 3.5vw, 36px);
  position: relative;
}

.signup-form::before {
  background: linear-gradient(90deg, #057a3d, #0ea35b);
  border-radius: 999px;
  content: "";
  height: 4px;
  left: 38px;
  position: absolute;
  right: 38px;
  top: -2px;
}

.form-header {
  align-items: center;
  display: flex;
  gap: 14px;
  margin-bottom: 22px;
}

.form-icon {
  align-items: center;
  background: #eaf7ef;
  border: 1px solid rgba(5, 122, 61, 0.16);
  border-radius: 16px;
  display: inline-flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.signup-form h2 {
  color: #111827;
  font-size: 24px;
  font-weight: 900;
  margin: 0;
}

.form-header p {
  color: #6b756f;
  font-size: 14px;
  margin: 3px 0 0;
}

.section-title {
  align-items: center;
  color: #1f6f4b;
  display: flex;
  font-size: 15px;
  font-weight: 900;
  gap: 8px;
  margin-bottom: 16px;
}

.signup-form ::v-deep .row {
  margin-bottom: 6px;
  row-gap: 8px;
}

.signup-form ::v-deep .row--dense > .col,
.signup-form ::v-deep .row--dense > [class*="col-"] {
  padding-bottom: 5px;
  padding-top: 5px;
}

.signup-form ::v-deep .v-input.signup-field {
  padding-top: 0;
}

.signup-form ::v-deep .v-input.signup-field.v-text-field--outlined {
  margin-bottom: 0;
}

.signup-form > .signup-field + .signup-field {
  margin-top: 10px;
}

.signup-form > .signup-field + .row {
  margin-top: 10px;
}

.signup-form ::v-deep .v-text-field--outlined fieldset,
.signup-form ::v-deep .v-select--outlined fieldset {
  border-color: #cfd9d2;
}

.signup-form ::v-deep .v-input--is-focused fieldset {
  border-color: #057a3d !important;
  border-width: 2px;
}

.signup-form ::v-deep .v-input__slot {
  border-radius: 12px;
  min-height: 54px;
}

.signup-form ::v-deep input {
  font-size: 16px;
}

.signup-form ::v-deep .v-label {
  font-size: 15px;
}

.signup-form ::v-deep .v-label {
  color: #6b7280;
}

.signup-form ::v-deep .v-input--is-focused .v-label {
  color: #057a3d;
}

.signup-submit {
  border-radius: 14px;
  font-size: 14px;
  font-weight: 900;
  margin-top: 18px;
}

.login-note {
  color: #68736d;
  margin: 18px 0 0;
  text-align: center;
}

.login-note a {
  color: #057a3d;
  font-weight: 800;
  text-decoration: none;
}

@media (max-width: 1280px) {
  .signup-page {
    padding-left: clamp(22px, 4vw, 48px);
    padding-right: clamp(22px, 4vw, 48px);
  }

  .signup-shell {
    gap: 42px;
    grid-template-columns: minmax(260px, 360px) minmax(0, 600px);
    max-width: 1020px;
  }

  h1 {
    font-size: clamp(42px, 5vw, 62px);
  }

  .signup-copy p {
    font-size: 17px;
  }
}

@media (max-width: 1060px) {
  .signup-topbar {
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
  }

  .signup-shell {
    grid-template-columns: minmax(0, 760px);
    justify-content: center;
    margin-top: 34px;
  }

  .signup-copy {
    padding-top: 0;
    text-align: center;
  }

  h1,
  .signup-copy p {
    margin-left: auto;
    margin-right: auto;
  }

  h1 {
    font-size: clamp(42px, 8vw, 56px);
    max-width: 640px;
  }

  .signup-copy p {
    max-width: 640px;
  }

  .signup-highlights {
    justify-content: center;
    margin-top: 22px;
  }

  .signup-form {
    width: 100%;
  }
}

@media (max-width: 900px) {
  .signup-page {
    padding: 20px 16px 36px;
  }

  .signup-copy {
    padding-top: 0;
  }

  h1 {
    font-size: clamp(38px, 10vw, 50px);
    margin-bottom: 18px;
  }
}

@media (max-width: 560px) {
  .signup-page {
    padding: 16px 12px 30px;
  }

  .brand {
    gap: 10px;
  }

  .signup-topbar {
    gap: 12px;
  }

  .signup-back {
    min-height: 36px;
    padding: 0 12px 0 10px;
  }

  .brand-mark {
    height: 44px;
    width: 44px;
  }

  .brand-text {
    font-size: 21px;
  }

  .signup-shell {
    gap: 24px;
    margin-top: 26px;
  }

  .eyebrow {
    font-size: 12px;
    margin-bottom: 12px;
  }

  h1 {
    font-size: clamp(36px, 12vw, 44px);
  }

  .signup-copy p {
    font-size: 15px;
    line-height: 1.45;
  }

  .signup-highlights {
    gap: 8px;
    margin-top: 18px;

    span {
      font-size: 12px;
      min-height: 34px;
      padding: 0 12px;
    }
  }

  .signup-form {
    border-radius: 20px;
    padding: 22px 16px;
  }

  .signup-form::before {
    left: 22px;
    right: 22px;
  }

  .form-header {
    align-items: flex-start;
    gap: 12px;
  }

  .form-icon {
    border-radius: 14px;
    height: 42px;
    min-width: 42px;
    width: 42px;
  }

  .signup-form h2 {
    font-size: 22px;
  }

  .form-header p {
    font-size: 13px;
  }

  .signup-form ::v-deep .v-input__slot {
    min-height: 52px;
  }
}
</style>
